import ErrorPage from '../modules/ErrorPage';
import Layout from '../modules/Layout';
import { SystemConfig, CustomNextPageContext } from '../lib/types';
import ErrorComponent, { ErrorProps } from 'next/error';
import { bootstrap } from '../lib/bootstrap';
import { getTheme } from '../lib/theme';
import { isAppContent } from '../lib/util';
import { BaseStyles } from '@urbaninfrastructure/react-ui-kit';
import { useRouter } from 'next/router';
import { captureException } from '../lib/sentry';
import { useStaticDataSystem } from '../lib/core/staticDataQuery';
import { ThemeKey } from '@urbaninfrastructure/design-tokens';

interface MyWindow extends Window {
  __NEXT_DATA__: any;
}

declare var window: MyWindow;

const CustomErrorComponent = ({
  statusCode,
  systemConfig,
  hasGetInitialPropsRun,
  err
}: ErrorProps & {
  err?: Error;
  systemConfig: SystemConfig;
  hasGetInitialPropsRun: boolean;
}) => {
  if (!hasGetInitialPropsRun && err) {
    // getInitialProps is not called in case of
    // https://github.com/zeit/next.js/issues/8592. As a workaround, we pass
    // err via _app.js so it can be captured
    captureException(err);
  }
  const router = useRouter();
  const staticDataSystem = useStaticDataSystem();
  const colorKey = (staticDataSystem.colourKey as ThemeKey) || undefined;
  const theme = getTheme({
    colorKey,
    isApp: isAppContent(router.query)
  });
  const errorComponent = (
    <ErrorPage
      statusCode={statusCode}
      errorType={statusCode === 404 ? 'pageNotFound' : 'unknown'}
      intercomId={systemConfig.intercomId}
    />
  );

  const baseStylesProps = {
    colorKey,
    theme,
    font: 'urban-grotesk' as const
  };

  if (statusCode === 404) {
    return (
      <BaseStyles {...baseStylesProps}>
        <Layout
          url={router}
          loading={false}
          render={() => {
            return errorComponent;
          }}
          title="Page not found"
        />
      </BaseStyles>
    );
  }
  return <BaseStyles {...baseStylesProps}>{errorComponent}</BaseStyles>;
};

CustomErrorComponent.getInitialProps = async (ctx: CustomNextPageContext) => {
  const { req, res, err, asPath } = ctx;
  const systemConfig = req
    ? req.systemConfig
    : window.__NEXT_DATA__.props.systemConfig;
  const errorInitialProps: ErrorProps & {
    hasGetInitialPropsRun?: boolean;
  } = await ErrorComponent.getInitialProps(ctx);

  // Workaround for https://github.com/zeit/next.js/issues/8592, mark when
  // getInitialProps has run
  errorInitialProps.hasGetInitialPropsRun = true;

  if (err && err.code === 'ENOENT') {
    errorInitialProps.statusCode = 404;
  }

  if (res) {
    // Running on the server, the response object is available.
    //
    // Next.js will pass an err on the server if a page's `getInitialProps`
    // threw or returned a Promise that rejected

    if (res.statusCode === 404) {
      // Opinionated: do not record an exception in Sentry for 404
      return { systemConfig, statusCode: 404 };
    }

    if (err) {
      captureException(err);
      return { ...errorInitialProps, systemConfig };
    }
  } else {
    // Running on the client (browser).
    //
    // Next.js will provide an err if:
    //
    //  - a page's `getInitialProps` threw or returned a Promise that rejected
    //  - an exception was thrown somewhere in the React lifecycle (render,
    //    componentDidMount, etc) that was caught by Next.js's React Error
    //    Boundary. Read more about what types of exceptions are caught by Error
    //    Boundaries: https://reactjs.org/docs/error-boundaries.html
    if (err) {
      captureException(err);

      return { ...errorInitialProps, systemConfig };
    }
  }

  // If this point is reached, getInitialProps was called without any
  // information about what the error might be. This is unexpected and may
  // indicate a bug introduced in Next.js, so record it in Sentry
  captureException(
    new Error(`_error.js getInitialProps missing data at path: ${asPath}`)
  );

  return { ...errorInitialProps, systemConfig };
};

export default bootstrap(CustomErrorComponent);
